<template>
  <div
    v-if="isShowModal"
    class="new-child"
  >
    <span class="new-child__close" @click="cancel"></span>
    <h2 class="new-child__title">Adding a <span class="capitalize">{{ camperStudent('singular') }}</span></h2>

    <b-spinner label="Loading" v-if="isLoading"></b-spinner>

    <div
      v-else
    >
      <RegistrationFormRow
        v-for="field in visibleFields"
        :key="field.id"
        :fields-data="field"
        :backend-validation-error="getBackendError(field.name)"
      />
    </div>

    <p v-if="errorMessage"
       class="form__error pt-4"
    >
      {{ errorMessage }}
    </p>

    <p v-if="serverMessage"
       class="form__error pt-4"
    >
      {{ serverMessage }}
    </p>

    <div
      v-if="validationErrors.length"
      class="form-group pt-1"
    >
      <p
        v-for="(validationError, index) in validationErrors"
        :key="index"
        class="text-center text-danger"
      >
        {{ validationError }}
      </p>
    </div>

    <div
      v-if="disabledInfoMessage && isSendButtonDisabled"
      class="form__disabled_error_message"
    >
      {{ disabledInfoMessage }}
    </div>

    <button
      class="btn btn--color-dark form__button"
      :class="{ 'btn--loading': isLoading }"
      @click="sendForm"
      :disabled="isLoading || isSendButtonDisabled"
    >
      Save
    </button>
  </div>
</template>

<script>
import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'

import 'vue2-datepicker/index.css'

import store from '@/store'
import { mapGetters } from 'vuex'
import RegistrationFormRow from '@/views/Registration/RegistrationFormRow.vue'
import { BSpinner } from 'bootstrap-vue'

export default {
  components: {
    RegistrationFormRow,
    BSpinner,
  },
  props: {
    isShowModal: {
      type: Boolean,
      default: false,
    },
    defaultGender: {
      type: String,
      default: 'Male',
    },
    familyName: {
      type: String,
      default: '',
    },
    familyId: {
      type: Number,
      default: null,
    },
  },
  mixins: [instanceSessionCamperNames],
  data() {
    return {
      backendErrors: {},
    }
  },
  watch: {
    isShowModal(val) {
      if (!val) {
        this.resetModalData()
      }
      if (val) {
        this.setFormsData()
      }
    },
  },
  computed: {
    ...mapGetters({
      preparedFields: 'addNewChild/getPreparedFields',
      hasFileField: 'addNewChild/getHasFileField',
      hasSignatureField: 'addNewChild/getHasSignatureField',
      registrationForm: 'addNewChild/getNewChildForm',
      disabledInfoMessage: 'addNewChild/getDisabledInfoMessage',
      isSendButtonDisabled: 'addNewChild/getIsSendButtonDisabled',
    }),
    visibleFields() {
      return this.preparedFields.filter(field => !field.hidden)
    },
    isLoading: {
      get: function () {
        return store.getters['addNewChild/getIsLoading']
      },
      set: function (val) {
        store.commit('addNewChild/SET_IS_LOADING', val)
      }
    },
    errorMessage: {
      get: function () {
        return store.getters['addNewChild/getErrorMessage']
      },
      set: function (val) {
        store.commit('addNewChild/SET_ERROR_MESSAGE', val)
      }
    },
    serverMessage: {
      get: function () {
        return store.getters['addNewChild/getServerMessage']
      },
      set: function (val) {
        store.commit('addNewChild/SET_SERVER_MESSAGE', val)
      }
    },
    validationErrors: {
      get: function () {
        return store.getters['addNewChild/getValidationErrors']
      },
      set: function (val) {
        store.commit('addNewChild/SET_VALIDATION_ERRORS', val)
      }
    },
    validTracker: {
      get: function () {
        return store.getters['addNewChild/getValidTracker']
      },
      set: function (val) {
        store.commit('addNewChild/SET_VALID_TRACKER', val)
      }
    },
  },
  async created() {
    this.isLoading = true

    try {
      await this.setNewChildForm()
      await this.setFormsData()
    } catch (error) {
      console.error('Failed to load new child form:', error)
      this.errorMessage = 'Unable to load the form. Please try again.'
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    async setNewChildForm() {
      await store.dispatch('addNewChild/fetchSchoolNewChildForm', {
        role: 'child',
        query: null,
      })
    },
    async setFormsData() {
      store.commit('addNewChild/SET_FAMILY_NAME', this.familyName)
      store.commit('addNewChild/SET_DEFAULT_GENDER', this.defaultGender)
      store.commit('addNewChild/SET_FORMS_DATA')
      store.commit('addNewChild/CHECK_CONDITIONS')
    },
    async validStep() {
      await this.$nextTick()

      if (this.preparedFields.filter(field =>
        field.required
        && !['plain_text', 'signature'].includes(field.type)
        && (field.value == null || field.value.length === 0)
        && !field.hidden).length > 0
      ) {
        this.errorMessage = 'Please, fill required fields'
        this.validTracker = true
        return
      }

      const signatureFields = this.preparedFields.filter(field => field.type === 'signature' && field.required && !field.hidden)
      if (signatureFields.length > 0) {
        signatureFields.forEach(field => {
          const canvasRef = this.findCanvasRefForField(field)
          if (canvasRef && canvasRef.isEmpty() && !canvasRef.loadedImage) {
            this.errorMessage = 'Please, fill required fields'
            this.validTracker = true
          }
        })
        if (this.validTracker) {
          return
        }
      }

      this.validTracker = false

      this.errorMessage = ''
      this.serverMessage = ''
      this.validationErrors = []
      this.backendErrors = {}
    },
    async sendForm() {
      this.validTracker = false
      await this.validStep()
      if (this.validTracker) return

      await this.createChild()
    },
    async createChild() {
      this.isLoading = true

      let fileFieldsCount = 0
      let avatarFieldsCount = 0

      let queryParams = {
        fields: [],
        form_data: {
          role: 'child',
          family_id: this.familyId,
        },
        registration_form_id: this.registrationForm.id,
        type: this.registrationForm.type,
      }

      const filesLinks = []
      const filesNames = []
      const avatarLinks = []
      const avatarNames = []

      if (this.hasFileField) {
        const fileFields = this.preparedFields.filter(row => ['avatar', 'file_upload'].includes(row.type) && row.value)

        if (fileFields.length > 0) {
          fileFields.forEach(field => {
            if (Array.isArray(field.value)) {
              Array.from(field.value).forEach(val => {
                if (val instanceof Blob) {
                  filesLinks.push(this.uploadFile(val))
                } else {
                  filesNames.push(val)
                }
              })
            } else {
              if (field.value instanceof Blob) {
                if (field.value instanceof Blob) {
                  avatarLinks.push(this.uploadFile(field.value, 'avatar'))
                } else {
                  avatarNames.push(field.value)
                }
              }
            }
          })
        }
      }

      if (this.hasSignatureField) {
        const signatureFields = this.preparedFields.filter(row => row.type === 'signature')
        signatureFields.forEach(field => {
          const canvasRef = this.findCanvasRefForField(field)
          if (canvasRef && !canvasRef.isEmpty()) {
            field.signature = canvasRef.save()
          }
        })
      }

      const requests = []

      const fileRequests = filesLinks.map(link => ({ request: link, type: 'file' }))
      const avatarRequests = avatarLinks.map(link => ({ request: link, type: 'avatar' }))

      requests.push(...fileRequests, ...avatarRequests)

      Promise.all(requests.map(r => r.request))
        .then(responses => responses.forEach((response, index) => {
          const request = requests[index]

          if (!response.file_path) {
            store.commit('SET_LOADING_STATUS', false)
            this.errorMessage = 'Error uploading file'
            throw new Error('Error')
          }

          if (request.type === 'file') {
            filesNames.push(response.file_path)
          } else if (request.type === 'avatar') {
            avatarNames.push(response.file_path)
          }
        }))
        .then(() => {

          this.preparedFields
            .filter(row => !row.hidden)
            .forEach(row => {
              if (row.type === 'file_upload' && row.value) {
                let from = fileFieldsCount
                let to = fileFieldsCount + row.value.length
                let sliced = filesNames.slice(from, to)

                queryParams.fields.push({
                  field_id: row.id,
                  files: sliced,
                })
                queryParams.form_data[row.name] = sliced

                fileFieldsCount = to
              } else if (row.type === 'avatar' && row.value) {
                let avatarName = avatarNames[avatarFieldsCount]

                queryParams.fields.push({
                  field_id: row.id,
                  files: [avatarName],
                })
                queryParams.form_data[row.name] = avatarName

                avatarFieldsCount++
              } else if (row.type === 'signature' && row.signature) {
                queryParams.fields.push({
                  field_id: row.id,
                  result: row.signature,
                })
                queryParams.form_data[row.name] = row.signature
              } else {
                queryParams.fields.push({
                  field_id: row.id,
                  result: row.value,
                })
                queryParams.form_data[row.name] = row.value
              }
            })

          store.dispatch('addNewChild/createOrUpdateChild', queryParams)
            .then(response => {
              this.$emit('updateChildrenList', response.data.data.id)
              this.closeModal()
            })
            .catch(error => {
              if (error.response) {
                if ( error.response.status === 500 ) this.serverMessage = 'Something went wrong. Please contact Administration for details.'
                if ( error.response.status === 422 ) {
                  const validationErrors = Object.values(error.response.data.errors)
                  this.validationErrors = validationErrors.flat(Infinity)
                  this.backendErrors = {...error.response.data.errors}
                }
              }
            })
            .finally(() => {
              this.isLoading = false
            })
        })
        .catch(error => {
          // Catches Promise.all rejections (a file/avatar upload request
          // failed or never resolved) and the missing-file_path guard above.
          // Without this, those paths never reach the inner dispatch/.finally()
          // and isLoading was left stuck true forever.
          console.error('Failed to upload child files:', error)
          if (!this.errorMessage) {
            this.errorMessage = 'Something went wrong while uploading files. Please try again.'
          }
          this.isLoading = false
        })
    },
    getBackendError(fieldName) {
      const key = `form_data.${fieldName}`

      return this.backendErrors[key] ? this.backendErrors[key][0] : null
    },
    findCanvasRefForField(field) {
      if (this.$refs[field.ref]) {
        return this.$refs[field.ref][0]?.$refs[`signature_${field.ref}`]?.$refs[field.ref]
      }
      return null
    },
    async uploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)

      return store.dispatch('files/uploadFiles', {
        name: `new-child-modal-files-${new Date().getTime()}`,
        file: formData,
      })
    },
    resetModalData() {
      store.commit('addNewChild/RESET_DATA')
    },
    closeModal() {
      this.resetModalData()
      this.$emit('closeModal')
    },
    cancel() {
      this.$emit('cancel')
      this.closeModal()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/colors/colors';

.new-child {
  position: fixed;
  border-radius: 10px;
  padding: 50px;
  width: 500px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: #FFF;
  z-index: 20;
  box-shadow: 0px 0px 0px 9999px rgba(0, 0, 0, 0.5);

  max-height: 90vh;
  overflow-y: scroll;
  transition: 0.5s;
  &::-webkit-scrollbar {
    width: 6px;
    height: 90%;
  }

  &::-webkit-scrollbar-button {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px #fff;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: $primary;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #69182d;
  }
  &__close {
    position: absolute;
    display: flex;
    align-items: center;
    top: 20px;
    right: 20px;
    width: 15px;
    height: 15px;
    cursor: pointer;

    &::before,&::after {
      content: '';
      display: block;
      position: absolute;
      left: 6px;
      width: 1px;
      height: 15px;
      background: #000;
      transform: rotate(45deg);
    }
    &::after{
      transform: rotate(-45deg);
    }
  }
  &__title {
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 30px;
  }

  &__avatar {
    display: flex;
    flex-direction: column;
    align-items: center;
    &-img {
      background: #EDEDED;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      margin-bottom: 10px;
      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
        border-radius: 50%;
      }
    }

    &-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      color: $primary;
      svg > g > path {
        stroke: $primary;
      }
      span {
        margin-left: 5px;
      }
    }
  }
}
@media screen and (max-width: 767px) {
  .new-child {
    position: absolute;
    width: 90%;
    padding: 20px;
    left: 5%;
    top: 20px;
    transform: none;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }
}
</style>
<style lang="scss">
.mx-datepicker{
  &.is-invalid{
    .input-date {
      border: 1px solid var(--error) !important;
      &::placeholder {
        color: var(--error)
      }
    } 
  }  
}
.new-child__avatar-img.is-invalid {
  border: 1px solid var(--error) !important;
  border-color: var(--error);
}
</style>